import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { connect, useDispatch } from "react-redux";

// ANT DESIGN IMPORTS
import { Input, Row, Typography, Card, Table, Spin, Select } from "antd";

// PROJECT IMPORTS
import MainLayout from "../../../components/layouts/MainLayout";
import TopContent from "../../../components/layouts/TopContent";
import { BOM } from "../../../services/definedMethod";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import DownloadTableButtons from "../../../components/DownloadTableButtons";
import { apiConfig, labelConfig, staticConfig } from "../security.config";
import { ArrowDownOutlined } from "@ant-design/icons";

const columns = [
  {
    title: labelConfig.id,
    dataIndex: "id",
  },
  {
    title: labelConfig.resourceId,
    dataIndex: "resourceId",
  },
  {
    title: (column) => (
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {labelConfig.branch}
        <div>
          <ArrowDownOutlined style={{ fontSize: 15, color: "gray" }} />
        </div>
      </div>
    ),
    dataIndex: "branch",
    sorter: (a, b) => a.branch.localeCompare(b.branch),
  },
  {
    title: labelConfig.status,
    dataIndex: "status",
  },
  {
    title: (column) => (
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {labelConfig.madeBy}
        <div>
          <ArrowDownOutlined style={{ fontSize: 15, color: "gray" }} />
        </div>
      </div>
    ),
    dataIndex: "madeBy",
    sorter: (a, b) => a.madeBy.localeCompare(b.madeBy),
  },

  {
    title: labelConfig.entity,
    dataIndex: "entity",
  },
  {
    title: labelConfig.userIp,
    dataIndex: "userIp",
  },
  {
    title: labelConfig.madeDate,
    dataIndex: "madeDate",
  },
  {
    title: labelConfig.madeDateTimeStamp,
    dataIndex: "madeDateTimeStamp",
  },
  {
    title: (column) => (
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {labelConfig.checker}
        <div>
          <ArrowDownOutlined style={{ fontSize: 15, color: "gray" }} />
        </div>
      </div>
    ),
    dataIndex: "checker",
    sorter: (a, b) => a.checker.localeCompare(b.checker),
  },
  {
    title: labelConfig.checkedDate,
    dataIndex: "checkedDate",
  },
  {
    title: labelConfig.checkedDateTimeStamp,
    dataIndex: "checkedDateTimeStamp",
  },
  {
    title: labelConfig.action,
    dataIndex: "action",
  },
];

const ManageAuditTrails = (props) => {
  const { userData, rightBtn, rightAction } = props;
  const { session } = userData;
  const { Text } = Typography;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // STATE VARIABLES FOR DATA & PAGINATION
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [limit, setLimit] = useState(15);
  const [auditTrail, setAuditTrailList] = useState([]);
  const [originalList, setOriginalList] = useState([]);
  const [offset, setOffset] = useState(0); // Offset for API
  const [currentPage, setCurrentPage] = useState(1); // Current page number
  // const limit = 14; // Page size (items per page)
  const [totalRecords, setTotalRecords] = useState(0); // Total record count

  //DECLARED METHODS
  function handleSearchInTable(e) {
    const searchKey = e.target.value;
    if (!searchKey) {
      setAuditTrailList(originalList);
      return;
    }
    const filterBySearch = originalList.filter((item) =>
      item?.madeBy?.toLowerCase().includes(searchKey.toLowerCase()),
    );
    setAuditTrailList(filterBySearch);
  }

  function selectTableCount(value) {
    setLimit(value);
  }

  const getAudit = (currentOffset = 0) => {
    setIsLoading(true);
    const params = {
      limit,
      offset: currentOffset,
      paged: true,
    };

    let body = { session, params };

    BOM.FetchReqAction(body, apiConfig.Audits, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
      } else {
        const objList = res.pageItems || [];
        const tempArray = objList.map((element) => ({
          key: element?.id,
          id: element?.id || "-",
          resourceId: element?.resourceId || "-",
          branch: element?.officeName || "-",
          status: element?.processingResult || "-",
          madeBy: element?.maker || "-",
          action: element?.actionName || "-",
          userIp: element?.clientIp || "-",
          madeDate: element?.madeOnDate ? BOM.DateTime(element?.madeOnDate, "only-date") : "-",
          madeDateTimeStamp: element?.madeOnDate
            ? BOM.DateTime(element?.madeOnDate, "with-seconds")
            : "-",
          checkedDate: element?.checkedOnDate
            ? BOM.DateTime(element?.checkedOnDate, "only-date")
            : "-",
          checkedDateTimeStamp: element?.checkedOnDate
            ? BOM.DateTime(element?.checkedOnDate, "with-seconds")
            : "-",
          entity: element?.entityName || "-",
          checker: element?.checker || "-",
        }));

        setAuditTrailList(tempArray);
        setOriginalList(tempArray);

        // If we received fewer than 'limit' records, this is the last page.
        if (objList.length < limit) {
          setTotalRecords(offset + objList.length);
        } else {
          // Otherwise, we might not know the total.
          // Here we set a dummy total that allows further paging.
          // Alternatively, if your API returns a total count, use that.
          setTotalRecords(offset + limit * 10);
        }
      }
      setIsLoading(false);
    });
  };

  const getCsvListData = () => {
    return new Promise((resolve, reject) => {
      const params = {
        limit: 1000,
        offset: 0,
        paged: false,
      };

      let body = {
        session,
        params,
      };

      BOM.FetchReqAction(body, apiConfig.Audits, (err, res) => {
        if (err) {
          reject(err);
        } else {
          const objList = res;
          const tempArray = objList.map((element) => ({
            key: element?.id,
            id: element?.id || "-",
            resourceId: element?.resourceId || "-",
            branch: element?.officeName || "-",
            status: element?.processingResult || "-",
            madeBy: element?.maker || "-",
            action: element?.actionName || "-",
            userIp: element?.clientIp || "-",
            madeDateTimeStamp: element?.madeOnDate
              ? BOM.DateTime(element?.madeOnDate, "with-seconds")
              : "-",
            madeDate: element?.madeOnDate ? BOM.DateTime(element?.madeOnDate, "only-date") : "-",
            entity: element?.entityName || "-",
            checker: element?.checker || "-",
            checkedDate: element?.checkedOnDate
              ? BOM.DateTime(element?.checkedOnDate, "only-date")
              : "-",
            checkedDateTimeStamp: element?.checkedOnDate
              ? BOM.DateTime(element?.checkedOnDate, "with-seconds")
              : "-",
          }));

          resolve(tempArray);
        }
      });
    });
  };

  useEffect(() => {
    getAudit(0);
    BOM.HasPermission("READ_AUDIT") && rightAction(cnt.RIGHT_SEARCH_BTN);
  }, []);

  // FLOAT METHODS
  function handleOnView() {
    delete selectedRow.amount;
    selectedRow.amount = selectedRow.amountToUse;
    const { detailBtn } = rightBtn;
    if (detailBtn.toLowerCase() === "primary") {
      navigate("/detail-audit-trail", { state: selectedRow });
    } else {
      console.log("Please choose a record from the table to open");
    }
  }

  function handleOnSearch() {
    const { searchBtn } = rightBtn;
    if (searchBtn.toLowerCase() === "primary") {
      navigate("/search-audit-trail", { ofObj: selectedRow });
    } else {
      console.log("Could not open the link");
    }
  }

  const handlePageChange = (page) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
    getAudit(newOffset);
  };

  const onSelectItemAction = (selectedRowKeys, selectedRows) => {
    setSelectedRow(selectedRows[0]);
    BOM.HasPermission("READ_AUDIT") && dispatch({ type: cnt.RIGHT_DETAIL_BTN });
  };

  return (
    <MainLayout isEnableRightSidebar={true} onView={handleOnView} onSearch={handleOnSearch}>
      <TopContent
        pageTitle={labelConfig.manageAuditTrail}
        pageItems={staticConfig.manageAuditTrailBreadcrumbItems}
      />
      <Card
        title={`${labelConfig.auditTrail} List`}
        style={{
          boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
          borderRadius: 20,
        }}
      >
        <Spin spinning={isLoading} size="large">
          <Row style={{ marginBottom: 20 }}>
            <div style={{ marginBottom: "auto" }}>
              <Text style={{ marginRight: 8 }}>Show</Text>
              <Select
                defaultValue={limit}
                options={[
                  { value: 5, label: "5" },
                  { value: 10, label: "10" },
                  { value: 15, label: "15" },
                ]}
                onSelect={selectTableCount}
              />
              <Text style={{ marginLeft: 8 }}>Entries</Text>
            </div>
            <div style={{ margin: "0px 50px" }}>
              <Input
                onChange={handleSearchInTable}
                placeholder="Search by User..."
                style={{ width: 400 }}
              />
            </div>
            {/* <DownloadTableButtons
              downloadData={auditTrail}
              columns={columns}
              fileName={"Audit Trail List"}
              onExportClick={getCsvListData}
            /> */}
          </Row>
          <Table
            rowSelection={{
              type: "radio",
              onChange: onSelectItemAction,
            }}
            columns={columns}
            dataSource={auditTrail}
            bordered
            pagination={{
              // position: "bottomCentre",
              current: currentPage,
              pageSize: limit,
              total: totalRecords,
              onChange: handlePageChange,
              showSizeChanger: false,
              showLessItems: false,
              // showQuickJumper: true,
              simple: false,

              // showTotal: (total, range) =>
              //   `Showing ${range[0]} - ${range[1]} of ${total} records`,
            }}
            scroll={{ x: 800 }}
          />
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});

const mapDispatchToProps = { rightAction };

export default connect(mapStateToProps, mapDispatchToProps)(ManageAuditTrails);
